import { clsx } from 'clsx';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { CONTEXT_KIND_ICONS } from './add-context/context-kind-icons';
import { presentSourceKinds, type SourceCounts, VIEW_KEYS } from './sources';
import { type AddContextMenuKind } from './types';

const ICON_STROKE_WIDTH = 1.75;

const COUNT_KEYS: Record<AddContextMenuKind, string> = {
  document: 'prop-chat.sources-count-document',
  finding: 'prop-chat.sources-count-finding',
  milestone: 'prop-chat.sources-count-milestone',
  'response-requirement': 'prop-chat.sources-count-response-requirement',
};

const ROW_TEXT =
  'font-[var(--ds-typography-font-definitions-font-family-body)] text-[length:var(--ds-typography-paragraph-mini-font-size)] font-[var(--ds-typography-paragraph-paragraph-weight)] leading-[var(--ds-typography-paragraph-mini-line-height)] tracking-[var(--ds-typography-paragraph-mini-letter-spacing)]';

interface SourcesMenuProps {
  /** Only called with at least one kind above zero. */
  counts: SourceCounts;
  onOpenList: () => void;
}

/**
 * What the info icon opens: a "Sources" heading and a single row that opens the
 * list in full.
 *
 * An answer grounded in one kind names it on the row, with its icon. One
 * grounded in several says "View all sources" and lists the kinds on a second
 * line, with no icon to pick between them.
 *
 * Sized and framed to sit inside the info tooltip's shell, which is stripped to
 * a transparent unpadded box for exactly this.
 */
export const SourcesMenu: FC<SourcesMenuProps> = ({ counts, onOpenList }) => {
  const { t } = useTranslation();
  const kinds = presentSourceKinds(counts);
  const total = kinds.reduce((sum, kind) => sum + counts[kind], 0);
  const singleKind = kinds.length === 1 ? kinds[0] : null;
  const Icon = singleKind ? CONTEXT_KIND_ICONS[singleKind] : null;

  return (
    <div className="w-[208px] overflow-hidden rounded-[var(--ds-radius-8)] border border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] shadow-[0_4px_3px_rgba(0,0,0,0.05),0_2px_2px_rgba(0,0,0,0.05)]">
      <div
        className={clsx(
          'flex min-h-[var(--ds-space-8)] w-full items-center bg-[var(--ds-color-bg-secondary)] px-[var(--ds-space-4)] text-[var(--ds-color-text-tertiary)]',
          ROW_TEXT,
        )}
      >
        {t('prop-chat.sources')}
      </div>
      <button
        type="button"
        onClick={onOpenList}
        className={clsx(
          'flex min-h-[var(--ds-space-8)] w-full cursor-pointer items-center gap-[var(--ds-space-1-5)] border-0 bg-[var(--ds-color-bg-primary)] px-[var(--ds-space-4)] py-[var(--ds-space-3)] text-left text-[var(--ds-color-text-primary)] hover:bg-[var(--ds-color-bg-secondary-dark)] focus-visible:bg-[var(--ds-color-bg-secondary-dark)] focus-visible:outline-none',
          ROW_TEXT,
        )}
      >
        {Icon && (
          <Icon
            aria-hidden="true"
            strokeWidth={ICON_STROKE_WIDTH}
            className="size-[var(--ds-space-4)] shrink-0 text-[var(--ds-color-bg-icon-primary)]"
          />
        )}
        {singleKind ? (
          <span className="min-w-0 flex-1 truncate">
            {t(VIEW_KEYS[singleKind], { count: total })}
          </span>
        ) : (
          <span className="flex min-w-0 flex-1 flex-col gap-[var(--ds-space-1)]">
            <span className="truncate">{t('prop-chat.view-all-sources', { count: total })}</span>
            <span className="truncate text-[var(--ds-color-text-tertiary)]">
              {kinds.map((kind) => t(COUNT_KEYS[kind], { count: counts[kind] })).join(', ')}
            </span>
          </span>
        )}
      </button>
    </div>
  );
};
